// src/components/WelcomeModal.js
//
// The moment after a member pays.
//
// Replaces the old success toast, which read as a receipt ("Your subscription
// is active") rather than a welcome. This says three things and gets out of
// the way: you're in, here's what you now have, and a personal note from
// Danielle setting the expectation that an email is coming.
//
// It deliberately asks NOTHING. An earlier revision carried an attribution
// question here ("How did you hear about us?"); the owner chose a warm note
// over a question at this moment. The capture plumbing was removed with it —
// see docs/decisions/adr-signup-attribution.md for how to revive it if the
// question is ever re-homed somewhere less precious than a member's first
// three seconds.
//
// zIndex 1100 sits deliberately ABOVE VideoPlayerModal (1000). A member who
// subscribes from a shared class link is handed to /workouts?recording=<id>,
// which opens the player modal at the same moment this one opens. The welcome
// wins that stack: it is one tap to clear, and class recordings do not
// autoplay, so nothing is playing underneath while it is up.

import React, { useEffect } from 'react';

export default function WelcomeModal({ open, content = {}, onClose }) {
    const c = content || {};

    // Escape closes — a modal that traps a member who just paid is the worst
    // possible first impression.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            onClick={() => onClose?.()}
            role="dialog"
            aria-modal="true"
            aria-label={c.welcomeModalHeading || "You're in!"}
            style={{
                position: 'fixed', inset: 0, zIndex: 1100,
                background: 'rgba(40,30,30,0.45)',
                backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20, boxSizing: 'border-box',
                animation: 'fadeIn 0.2s ease-out',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'relative',
                    background: 'rgba(255,255,255,0.96)',
                    backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                    borderRadius: 24,
                    border: '1px solid rgba(255,255,255,0.6)',
                    boxShadow: 'var(--shadow-xl)',
                    width: '100%', maxWidth: 420,
                    maxHeight: '88vh', overflowY: 'auto',
                    padding: '34px 28px 26px',
                    boxSizing: 'border-box',
                    animation: 'modalIn 0.25s ease-out',
                }}
            >
                <button
                    type="button"
                    onClick={() => onClose?.()}
                    aria-label="Close"
                    style={{
                        position: 'absolute', top: 12, right: 12,
                        width: 34, height: 34, borderRadius: '50%',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        // Derived, not --mom-warm-gray: some admin palettes set
                        // warm-gray equal to charcoal, which would render this
                        // utility glyph at full heading strength.
                        color: 'color-mix(in srgb, var(--mom-charcoal) 55%, var(--mom-cream))',
                    }}
                >
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                        <path d="M1.5 1.5l12 12M13.5 1.5l-12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                </button>

                <h2 style={{
                    fontFamily: 'var(--mom-font-serif)', fontSize: 30, fontStyle: 'italic',
                    color: 'var(--mom-charcoal)', margin: '0 0 12px',
                    textAlign: 'center', lineHeight: 1.25,
                }}>
                    {c.welcomeModalHeading || "You're in!"}
                </h2>

                {/* The only saturated colour on the card. */}
                <div style={{
                    width: 40, height: 2, borderRadius: 2, margin: '0 auto 16px',
                    background: 'var(--mom-primary)', opacity: 0.7,
                }} />

                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 15,
                    color: 'var(--mom-charcoal)', textAlign: 'center',
                    lineHeight: 1.55, margin: '0 0 20px',
                }}>
                    {c.welcomeModalSubtext || 'You now have access to all classes.'}
                </p>

                {/* Danielle's aside. Carried by whitespace and a shift into
                    italic serif rather than a tinted panel — the restrained
                    option the owner chose. The voice change is what separates
                    a personal note from product copy; no fill required. */}
                <p style={{
                    fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 15,
                    // Derived from charcoal rather than --mom-warm-gray: the live
                    // palette sets warm-gray equal to charcoal, which would render
                    // this at full heading strength and flatten the hierarchy.
                    color: 'color-mix(in srgb, var(--mom-charcoal) 78%, var(--mom-cream))',
                    textAlign: 'center', lineHeight: 1.65, margin: '4px 0 24px',
                    padding: '0 6px',
                }}>
                    {c.welcomeModalNote || 'Keep an eye out for a welcome email from me. Thanks so much for being here!'}
                </p>

                <button
                    type="button"
                    onClick={() => onClose?.()}
                    style={{
                        width: '100%', padding: '14px', borderRadius: 50,
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 700,
                        color: '#fff', border: 'none', cursor: 'pointer',
                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                        boxShadow: '0 2px 8px color-mix(in srgb, var(--mom-primary) 20%, transparent)',
                    }}
                >
                    {c.welcomeModalButtonLabel || "Let's go"}
                </button>
            </div>
        </div>
    );
}
